import { Program } from 'models/user';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import selectors from 'state/selectors';
import { modalActionType, moduleProgramCode } from 'utils/constants';
import actions from 'state/actions';
import { useTheme } from 'providers/ThemeProvider';
import MenuOption from './MenuOption';
import { StyledMenu, MenuContainer } from './styles';
import MenuSearch from './MenuSearch';
import MenuContent from './MenuContent';
import MenuFooter from './MenuFooter';

const Menu = () => {
  const [searchResults, setSearchResults] = useState<any>([]);
  const menuPrograms = useSelector(selectors.user.getMenuPrograms);
  const beBuildNumber = useSelector(selectors.app.getBuildNumber);
  const isMenuOpen = useSelector(selectors.app.getMenu);
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const iconColor = theme.colorPalette.grey.grey9;

  const chatbotMode = useSelector(selectors.chatbot.getDisplayMode);
  const isBotShowen = useSelector(selectors.chatbot.getIsOpen);

  const openChatbot = () => {
    dispatch(actions.chatbot.setOpen(true));
    dispatch(actions.chatbot.setMinimized(false));
    if (chatbotMode !== 'embedded') dispatch(actions.chatbot.setDisplayMode('floating'));
  };

  // Helper function to find LucyBOT in nested structure
  const findLucyBot = useCallback((programs: Program[]): Program | undefined => {
    for (const program of programs) {
      if (program.idProgram === moduleProgramCode.chatbot) {
        return program;
      }
      if (program.children && program.children.length > 0) {
        const found = findLucyBot(program.children);
        if (found) return found;
      }
    }
    return undefined;
  }, []);

  // Helper function to remove LucyBot from nested structure
  const removeLucyBot = useCallback((programs: Program[]): Program[] => {
    return programs.map((program) => ({
      ...program,
      children: program.children
        ? program.children.filter((child) => child.idProgram !== moduleProgramCode.chatbot)
        : [],
    }));
  }, []);

  // Memoize the filtered programs
  const { lucyBotProgram, regularPrograms } = useMemo(() => {
    const lucyBot = findLucyBot(menuPrograms || []);
    const regular = removeLucyBot(menuPrograms || []);
    return {
      lucyBotProgram: lucyBot,
      regularPrograms: regular,
    };
  }, [menuPrograms, findLucyBot, removeLucyBot]);

  useEffect(() => {
    setSearchResults(regularPrograms);
  }, [regularPrograms]);

  useEffect(() => {
    if (isMenuOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isMenuOpen]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value.toLowerCase();
      if (regularPrograms) {
        const results = regularPrograms.map((parent) => ({
          ...parent,
          children: parent.children.filter((child) => child.programName.toLowerCase().includes(input)),
        }));
        const final = results.filter(
          ({ children, programName }) => children.length > 0 || programName.toLowerCase().includes(input),
        );
        setSearchResults(final);
      }
    },
    [regularPrograms],
  );

  useEffect(() => {
    if (!isMenuOpen) {
      setSearchResults(regularPrograms);
    }
  }, [isMenuOpen, regularPrograms]);

  const handleSearchIconClick = useCallback(() => {
    dispatch(actions.app.toggleMenu());
  }, [dispatch]);

  const handleChangelogClick = useCallback(() => {
    dispatch(actions.modal.openModal(modalActionType.CHANGELOG));
  }, [dispatch]);

  return (
    <StyledMenu data-walkthrough="sidebar">
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <>
          <MenuSearch
            isMenuOpen={isMenuOpen}
            iconColor={iconColor}
            onChange={handleSearch}
            onIconClick={handleSearchIconClick}
            ref={searchInputRef}
          />
          {lucyBotProgram && (
            <MenuContainer isMenuOpen={isMenuOpen}>
              <div onClick={openChatbot}>
                <MenuOption
                  text="LucyBot"
                  isActive={isBotShowen}
                  id={lucyBotProgram.idProgram}
                  isFirst={true}
                  isMenuOpen={isMenuOpen}
                />
              </div>
            </MenuContainer>
          )}
          <MenuContent elements={searchResults} isMenuOpen={isMenuOpen} />
        </>
        <MenuFooter isMenuOpen={isMenuOpen} beBuildNumber={beBuildNumber} onChangelogClick={handleChangelogClick} />
      </div>
    </StyledMenu>
  );
};

export default Menu;
